// What the plugin keeps, all of it derived and regenerable:
// - `history-openings/path.json` inside a game's folder: the game's opening path.
// - `index.json` in the plugin's own storage: one entry per examined game,
//   so a search reads one file and never opens a folder.
import type { Storage } from '@chess/api-storage';
import { emptyIndex, isCurrent, type OpeningIndex, type OpeningPath } from './paths.ts';

export const PATH_FILE = 'path.json';
export const INDEX_FILE = 'index.json';
/** The subfolder this plugin keeps in a game. */
export const FOLDER = 'history-openings';

export async function readPath(folder: Storage): Promise<OpeningPath | undefined> {
  const text = await folder.readText(PATH_FILE);
  if (!text) return undefined;
  try {
    const value = JSON.parse(text) as Partial<OpeningPath>;
    return isCurrent(value) && Array.isArray(value.path) ? (value as OpeningPath) : undefined;
  } catch {
    return undefined;
  }
}

export async function writePath(folder: Storage, path: OpeningPath): Promise<void> {
  await folder.write(PATH_FILE, JSON.stringify(path));
}

/** The index if it was made by this version, else a fresh one. */
export async function readIndex(storage: Storage): Promise<OpeningIndex> {
  const text = await storage.readText(INDEX_FILE);
  if (!text) return emptyIndex();
  try {
    const value = JSON.parse(text) as Partial<OpeningIndex>;
    return isCurrent(value) && value.entries && typeof value.entries === 'object' ? (value as OpeningIndex) : emptyIndex();
  } catch {
    return emptyIndex();
  }
}

export async function writeIndex(storage: Storage, index: OpeningIndex): Promise<void> {
  await storage.write(INDEX_FILE, JSON.stringify(index));
}
